import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, Input, OnChanges, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FixtureViewType } from '@cds/betting-offer';
import { EventParticipant } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { PropType } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ModalRef } from '@frontend/sports/modal/feature';
import { IntlService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';

import { IBetslipRootState } from '../../../../betslip/base/store/state';
import { BetslipBetBuilderPick } from '../../../../betslip/core/picks/betslip-bet-builder-pick';
import { betslipPicksListSelector } from '../../../../betslip/modules/picks/selectors';
import { BetBuilderEventNameService } from '../../services/bet-builder-event-name.service';
import { BetBuilderHowToDialogService } from '../../services/bet-builder-how-to-dialog.service';
import { BetBuilderOverlayService } from '../../services/bet-builder-overlay.service';

@Component({
    selector: 'ms-betbuilder-overlay-header',
    templateUrl: './bet-builder-overlay-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconCustomComponent],
})
export class BetBuilderOverlayHeaderComponent implements OnInit, OnChanges {
    sitecore = inject(Sitecore);
    private betBuilderOverlayService = inject(BetBuilderOverlayService);
    private betBuilderHowToDialogService = inject(BetBuilderHowToDialogService);
    private betBuilderEventNameService = inject(BetBuilderEventNameService);
    private store = inject<Store<IBetslipRootState>>(Store);
    private intlService = inject(IntlService);

    @HostBinding('class') className = 'betbuilder-overlay--header';

    @Input() sportId: number;
    @Input() leagueId: number;
    @Input() eventId: string;

    @Input() eventName: string;
    @Input() eventStartDate: Date;
    @Input() eventParticipants?: EventParticipant[];
    @Input() eventViewType?: FixtureViewType;

    betbuilderMessages: PropType<PropType<Sitecore, 'betBuilderEntainUI'>, 'messages'>;
    modalHandler: ModalRef | null = null;
    betBuilderPick: BetslipBetBuilderPick | undefined;
    headerParticipants: string;
    private readonly destroyRef = inject(DestroyRef);

    get date(): string | undefined {
        return this.intlService.formatDate(this.eventStartDate, 'shortDate');
    }

    constructor() {
        const sitecore = this.sitecore;

        this.betbuilderMessages = sitecore.betBuilderEntainUI.messages;
    }

    ngOnInit(): void {
        this.store
            .select(betslipPicksListSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((picks) => {
                this.betBuilderPick = (picks.filter((pick) => BetslipBetBuilderPick.isEntainUiBetBuilderPick(pick)) as BetslipBetBuilderPick[]).find(
                    (pick) => pick.eventId === this.eventId,
                );
            });
    }

    ngOnChanges(changes: ISimpleChanges<BetBuilderOverlayHeaderComponent>): void {
        if (changes.eventParticipants?.currentValue && changes.eventViewType?.currentValue) {
            this.headerParticipants = this.betBuilderEventNameService.getParticipantHeaderTitle({
                participants: this.eventParticipants!,
                eventViewType: this.eventViewType,
                leagueId: this.leagueId,
                sportId: this.sportId,
                eventName: this.eventName,
            });
        } else if (changes.eventName?.currentValue) {
            this.headerParticipants = changes.eventName.currentValue;
        }
    }

    closeOverlayAndKeepPick(): void {
        this.betBuilderOverlayService.close({ sportId: this.sportId, leagueId: this.leagueId, eventName: this.headerParticipants });
    }

    openHowToPopup(): void {
        this.betBuilderHowToDialogService.openBuilder({
            leagueId: this.leagueId,
            sportId: this.sportId,
            eventName: this.headerParticipants,
        });
    }
}
